import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, Filter, Sparkles, Trash2, Tag, Download, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { leadsApi } from "@/lib/leadsApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Avatar, ScoreBar, StatusBadge, TinyTag } from "@/components/find/atoms";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const PAGE_SIZE = 25;

export default function LeadsList() {
    const { t } = useI18n();
    const { refresh } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: PAGE_SIZE });
    const [loading, setLoading] = useState(true);
    const [seeding, setSeeding] = useState(false);
    const [facets, setFacets] = useState({ industries: [], sizes: [], countries: [], seniorities: [], funding_stages: [], statuses: [], tags: [] });
    const [selected, setSelected] = useState(new Set());
    const [filters, setFilters] = useState({
        search: "",
        industries: [], sizes: [], countries: [], seniorities: [], funding_stages: [], statuses: [],
        min_score: null,
    });
    const [sort, setSort] = useState("-score");
    const [page, setPage] = useState(1);

    useEffect(() => {
        leadsApi.facets().then(setFacets).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = { sort, page, page_size: PAGE_SIZE };
        if (filters.search) params.search = filters.search;
        for (const k of ["industries", "sizes", "countries", "seniorities", "funding_stages", "statuses"]) {
            if (filters[k].length) params[k] = filters[k].join(",");
        }
        if (filters.min_score != null) params.min_score = filters.min_score;
        leadsApi.list(params)
            .then((d) => { setData(d); setSelected(new Set()); })
            .catch(() => toast.error("Failed to load leads"))
            .finally(() => setLoading(false));
    }, [filters, sort, page]);

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
    const activeFilterCount = useMemo(() => {
        let n = 0;
        for (const k of ["industries", "sizes", "countries", "seniorities", "funding_stages", "statuses"]) {
            n += filters[k].length;
        }
        if (filters.min_score != null) n++;
        return n;
    }, [filters]);

    async function handleSeed() {
        setSeeding(true);
        try {
            const res = await leadsApi.seed(500, false);
            if (res.skipped) toast.info(`Already have ${res.existing} leads`);
            else toast.success(`Seeded ${res.inserted} demo leads`);
            setPage(1);
            setFilters({ ...filters });
        } catch (_e) {
            toast.error("Seed failed");
        } finally {
            setSeeding(false);
        }
    }

    function toggleSelect(id) {
        const next = new Set(selected);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelected(next);
    }
    function toggleAll() {
        if (selected.size === data.items.length) setSelected(new Set());
        else setSelected(new Set(data.items.map((l) => l.lead_id)));
    }

    async function handleBulkDelete() {
        if (!selected.size) return;
        if (!window.confirm(`Delete ${selected.size} leads?`)) return;
        try {
            await leadsApi.bulkDelete([...selected]);
            toast.success(`${selected.size} leads deleted`);
            setSelected(new Set());
            setFilters({ ...filters });
        } catch (_e) {
            toast.error("Bulk delete failed");
        }
    }
    async function handleBulkTag() {
        if (!selected.size) return;
        const tag = window.prompt("Tag to add:");
        if (!tag) return;
        try {
            await leadsApi.bulkTag([...selected], tag);
            toast.success(`Tag applied`);
            setFilters({ ...filters });
        } catch (_e) {
            toast.error("Bulk tag failed");
        }
    }
    async function handleReveal(l, field) {
        try {
            const res = await leadsApi.reveal(l.lead_id, field);
            toast.success(`${field === "email" ? "Email" : "Phone"} revealed`);
            refresh();
            setData({ ...data, items: data.items.map((x) => x.lead_id === l.lead_id ? res.lead : x) });
        } catch (e) {
            const msg = e?.response?.data?.detail || "Failed";
            toast.error(msg);
        }
    }
    function exportCsv() {
        const rows = data.items;
        if (!rows.length) return;
        const cols = ["full_name", "title", "company_name", "industry", "company_size", "country", "email", "phone", "score", "status"];
        const csv = [cols.join(",")].concat(
            rows.map((r) => cols.map((c) => JSON.stringify(r[c] ?? "")).join(","))
        ).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `boxeb2b-leads-page${data.page}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    const isEmpty = !loading && data.total === 0 && !filters.search && !activeFilterCount;

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <h1 className="font-display font-black text-3xl tracking-tighter">{t.find.header.title}</h1>
                    <p className="mt-1 text-sm text-zinc-500">{t.find.header.subtitle}</p>
                </div>
                <div className="text-xs font-mono text-zinc-500" data-testid="leads-total">
                    {t.find.table.total(data.total)}
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                <div className="relative flex-1 min-w-[260px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        type="text"
                        value={filters.search}
                        onChange={(e) => { setPage(1); setFilters({ ...filters, search: e.target.value }); }}
                        placeholder={t.find.table.search}
                        data-testid="leads-search"
                        className="w-full pl-9 pr-3 py-2 bg-brand-surface border border-white/8 rounded-lg text-sm placeholder:text-zinc-500 focus:border-brand-indigo focus:ring-1 focus:ring-brand-indigo outline-none"
                    />
                </div>
                <FiltersPopover facets={facets} filters={filters} setFilters={(f) => { setPage(1); setFilters(f); }} activeCount={activeFilterCount} />
                <Select value={sort} onValueChange={(v) => { setPage(1); setSort(v); }}>
                    <SelectTrigger className="w-[180px] bg-brand-surface border-white/8 h-10" data-testid="leads-sort">
                        <SelectValue placeholder={t.find.table.sort} />
                    </SelectTrigger>
                    <SelectContent className="bg-brand-surface border-white/10 text-white">
                        <SelectItem value="-score">Score ↓</SelectItem>
                        <SelectItem value="score">Score ↑</SelectItem>
                        <SelectItem value="-updated_at">Recently updated</SelectItem>
                        <SelectItem value="full_name">Name A→Z</SelectItem>
                        <SelectItem value="company_name">Company A→Z</SelectItem>
                    </SelectContent>
                </Select>
                <button
                    type="button"
                    onClick={exportCsv}
                    data-testid="leads-export"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 text-sm text-zinc-200 transition"
                >
                    <Download size={14} /> {t.find.table.exportCsv}
                </button>
            </div>

            {/* Bulk bar */}
            <AnimatePresence>
                {selected.size > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="mb-3 flex items-center gap-2 bg-brand-indigo/10 border border-brand-indigo/30 rounded-lg px-3 py-2"
                        data-testid="leads-bulk-bar"
                    >
                        <span className="text-sm text-white">{t.find.table.selected(selected.size)}</span>
                        <div className="flex-1" />
                        <button type="button" onClick={handleBulkTag} data-testid="leads-bulk-tag" className="text-sm text-zinc-200 hover:text-white inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-white/5">
                            <Tag size={13} /> {t.find.table.bulkTag}
                        </button>
                        <button type="button" onClick={handleBulkDelete} data-testid="leads-bulk-delete" className="text-sm text-rose-300 hover:text-rose-200 inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-rose-500/10">
                            <Trash2 size={13} /> {t.find.table.bulkDelete}
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Table */}
            <div className="rounded-2xl border border-white/8 bg-brand-surface overflow-hidden">
                {loading ? (
                    <div className="py-24 flex items-center justify-center text-zinc-500 text-sm">
                        <Loader2 size={18} className="animate-spin mr-2" /> Loading…
                    </div>
                ) : isEmpty ? (
                    <div className="py-24 text-center">
                        <div className="mx-auto w-14 h-14 rounded-2xl btn-cta flex items-center justify-center mb-4">
                            <Sparkles size={22} className="text-white" />
                        </div>
                        <h3 className="font-display font-bold text-xl">{t.find.table.empty}</h3>
                        <p className="mt-1 text-sm text-zinc-500 max-w-sm mx-auto">{t.find.table.emptyBody}</p>
                        <div className="mt-6 flex items-center justify-center gap-2">
                            <button
                                type="button"
                                onClick={handleSeed}
                                disabled={seeding}
                                data-testid="leads-seed"
                                className="btn-cta text-white font-semibold px-5 py-2.5 rounded-full text-sm inline-flex items-center gap-2 disabled:opacity-60"
                            >
                                {seeding ? <><Loader2 size={14} className="animate-spin" /> {t.find.table.seeding}</> : <>{t.find.table.seed}</>}
                            </button>
                            <Link
                                to="/app/find/import"
                                className="px-5 py-2.5 rounded-full text-sm text-white border border-white/12 hover:border-white/25 hover:bg-white/5 transition"
                            >
                                {t.find.tabs.import}
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-xs uppercase tracking-wide text-zinc-500 border-b border-white/8 bg-white/[0.02]">
                                <tr>
                                    <th className="w-10 p-3">
                                        <Checkbox
                                            checked={data.items.length > 0 && selected.size === data.items.length}
                                            onCheckedChange={toggleAll}
                                            data-testid="leads-select-all"
                                        />
                                    </th>
                                    <th className="p-3 text-left">{t.find.table.columns.lead}</th>
                                    <th className="p-3 text-left">{t.find.table.columns.company}</th>
                                    <th className="p-3 text-left hidden md:table-cell">{t.find.table.columns.location}</th>
                                    <th className="p-3 text-left">{t.find.table.columns.score}</th>
                                    <th className="p-3 text-left hidden lg:table-cell">{t.find.table.columns.tags}</th>
                                    <th className="p-3 text-left">{t.find.table.columns.status}</th>
                                    <th className="p-3 w-10"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.items.map((l) => (
                                    <tr
                                        key={l.lead_id}
                                        className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer group"
                                        data-testid={`lead-row-${l.lead_id}`}
                                        onClick={() => navigate(`/app/find/leads/${l.lead_id}`)}
                                    >
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selected.has(l.lead_id)}
                                                onCheckedChange={() => toggleSelect(l.lead_id)}
                                                data-testid={`lead-select-${l.lead_id}`}
                                            />
                                        </td>
                                        <td className="p-3">
                                            <div className="flex items-center gap-3">
                                                <Avatar seed={l.avatar_seed} />
                                                <div className="min-w-0">
                                                    <div className="text-white font-medium truncate">{l.full_name}</div>
                                                    <div className="text-xs text-zinc-500 truncate">{l.title}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <div className="text-white truncate">{l.company_name}</div>
                                            <div className="text-xs text-zinc-500 truncate">{l.industry} · {l.company_size}</div>
                                        </td>
                                        <td className="p-3 hidden md:table-cell">
                                            <div className="text-zinc-300">{l.city}</div>
                                            <div className="text-xs text-zinc-500">{l.country}</div>
                                        </td>
                                        <td className="p-3">
                                            <ScoreBar score={l.score} />
                                        </td>
                                        <td className="p-3 hidden lg:table-cell">
                                            <div className="flex flex-wrap gap-1">
                                                {(l.tags || []).slice(0, 3).map((tg) => <TinyTag key={tg}>{tg}</TinyTag>)}
                                            </div>
                                        </td>
                                        <td className="p-3">
                                            <StatusBadge status={l.status} label={t.find.statuses[l.status] || l.status} />
                                        </td>
                                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                                            {!l.email && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleReveal(l, "email")}
                                                    data-testid={`lead-reveal-${l.lead_id}`}
                                                    className="text-[11px] font-mono badge-cyan px-2 py-1 rounded-md hover:opacity-90"
                                                    title={t.find.table.revealEmail}
                                                >
                                                    1c
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {data.total > PAGE_SIZE && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-white/8 text-sm text-zinc-400">
                        <div className="font-mono text-xs">
                            {t.find.table.page} {data.page} {t.find.table.of} {totalPages}
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setPage(Math.max(1, page - 1))}
                                disabled={page <= 1}
                                data-testid="leads-page-prev"
                                className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30"
                            >
                                <ChevronLeft size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => setPage(Math.min(totalPages, page + 1))}
                                disabled={page >= totalPages}
                                data-testid="leads-page-next"
                                className="p-1.5 rounded hover:bg-white/5 disabled:opacity-30"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Filters Popover ----------

function FiltersPopover({ facets, filters, setFilters, activeCount }) {
    const { t } = useI18n();
    function toggle(key, val) {
        const cur = filters[key] || [];
        const has = cur.includes(val);
        setFilters({ ...filters, [key]: has ? cur.filter((v) => v !== val) : [...cur, val] });
    }
    function clearAll() {
        setFilters({
            search: filters.search,
            industries: [], sizes: [], countries: [], seniorities: [], funding_stages: [], statuses: [],
            min_score: null,
        });
    }
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="leads-filters-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/25 hover:bg-white/5 text-sm text-zinc-200 transition"
                >
                    <Filter size={14} />
                    <span>{t.find.table.filters}</span>
                    {activeCount > 0 && (
                        <span className="badge-cyan text-[10px] font-mono px-1.5 py-0.5 rounded">{activeCount}</span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="leads-filters-popover"
                align="start"
                className="w-[440px] max-h-[70vh] overflow-y-auto p-4 bg-brand-surface border-white/10 text-white"
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">Filters</div>
                    {activeCount > 0 && (
                        <button type="button" onClick={clearAll} className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1" data-testid="leads-filters-clear">
                            <X size={12} /> {t.find.table.clear}
                        </button>
                    )}
                </div>
                <FilterGroup title={t.find.filterGroups.industries} options={facets.industries} selected={filters.industries} onToggle={(v) => toggle("industries", v)} testKey="industries" />
                <FilterGroup title={t.find.filterGroups.sizes} options={facets.sizes} selected={filters.sizes} onToggle={(v) => toggle("sizes", v)} testKey="sizes" />
                <FilterGroup title={t.find.filterGroups.countries} options={facets.countries} selected={filters.countries} onToggle={(v) => toggle("countries", v)} testKey="countries" />
                <FilterGroup title={t.find.filterGroups.seniorities} options={facets.seniorities} selected={filters.seniorities} onToggle={(v) => toggle("seniorities", v)} testKey="seniorities" labels={t.find.seniorities} />
                <FilterGroup title={t.find.filterGroups.funding_stages} options={facets.funding_stages} selected={filters.funding_stages} onToggle={(v) => toggle("funding_stages", v)} testKey="funding_stages" />
                <FilterGroup title={t.find.filterGroups.statuses} options={facets.statuses} selected={filters.statuses} onToggle={(v) => toggle("statuses", v)} testKey="statuses" labels={t.find.statuses} />

                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{t.find.filterGroups.min_score}</div>
                    <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={filters.min_score ?? 0}
                        onChange={(e) => setFilters({ ...filters, min_score: parseInt(e.target.value, 10) || null })}
                        data-testid="leads-min-score"
                        className="w-full accent-brand-indigo"
                    />
                    <div className="text-xs text-zinc-400 font-mono">{filters.min_score ?? 0}</div>
                </div>
            </PopoverContent>
        </Popover>
    );
}

function FilterGroup({ title, options, selected, onToggle, testKey, labels }) {
    if (!options?.length) return null;
    return (
        <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{title}</div>
            <div className="flex flex-wrap gap-1.5">
                {options.map((v) => {
                    const on = selected.includes(v);
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onToggle(v)}
                            data-testid={`filter-${testKey}-${v}`}
                            className={`text-xs px-2.5 py-1 rounded-full border transition ${
                                on
                                    ? "bg-brand-indigo text-white border-brand-indigo"
                                    : "bg-white/[0.04] text-zinc-300 border-white/10 hover:border-white/25"
                            }`}
                        >
                            {labels?.[v] || v}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
